/*

  Channel.ts

  Utility functions for working with Firebase

  Copyright (C) 2024 Miðeind ehf.
  Original author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import { getAnalytics, logEvent as firebaseLogEvent, Analytics } from 'firebase/analytics';
import { getAuth, signInWithCustomToken, onAuthStateChanged, Auth } from 'firebase/auth';
import { Database, getDatabase, off, onDisconnect, onValue, push, ref, remove, set, get, runTransaction } from 'firebase/database';

import { GlobalState } from "./globalstate";

let app: FirebaseApp;
let auth: Auth;
let database: Database;
let analytics: Analytics;

// Queue of callbacks to execute when database is ready
let databaseReadyQueue: (() => void)[] = [];

/**
 * Execute all queued callbacks now that database is ready
 */
function flushDatabaseReadyQueue(state: GlobalState) {
  const queue = [...databaseReadyQueue];
  databaseReadyQueue = []; // Clear queue before executing to avoid re-queuing
  queue.forEach(callback => callback());
  // Also notify the global callback if set
  state.onFirebaseReady?.();
}

function initFirebase(state: GlobalState): boolean {
  try {
    const {
      projectId,
      firebaseApiKey,
      databaseUrl,
      firebaseSenderId,
      firebaseAppId,
      measurementId
    } = state;
    const firebaseOptions: FirebaseOptions = {
      projectId,
      apiKey: firebaseApiKey,
      authDomain: `${projectId}.firebaseapp.com`,
      databaseURL: databaseUrl,
      storageBucket: `${projectId}.firebasestorage.app`,
      messagingSenderId: firebaseSenderId,
      appId: firebaseAppId,
      measurementId,
    };
    app = initializeApp(firebaseOptions, projectId);
    if (!app) {
      console.error("Failed to initialize Firebase");
      return false;
    }
  } catch (e) {
    console.error("Failed to initialize Firebase", e);
    return false;
  }
  return true;
}

export function isFirebaseAuthenticated(state: GlobalState): boolean {
  // Check if Firebase is currently authenticated
  // If auth is not initialized but state is provided, try to initialize
  if (!auth) {
    if (!app) initFirebase(state);
    if (app) auth = getAuth(app);
  }
  if (!auth || auth.currentUser === null) {
    return false;
  }

  // If auth is valid but database not initialized, initialize it
  // This handles the case where the user has cached auth but returns
  // directly to a route that needs Firebase Database (e.g., Gáta Dagsins)
  if (!database && app) {
    database = getDatabase(app);
    if (!database) {
      console.error("Failed to initialize Firebase Database");
      return false;
    }
    // Database initialized successfully - flush queued callbacks
    flushDatabaseReadyQueue(state);
    // Also initialize analytics if not already done
    if (!analytics) {
      analytics = getAnalytics(app);
      if (!analytics) {
        console.error("Failed to initialize Firebase Analytics");
        // We don't return false here since analytics is not critical
      }
    }
  }
  return true;
}

export async function loginFirebase(
  state: GlobalState,
  firebaseToken: string,
  onLoginFunc?: () => void
) {
  if (!app && !initFirebase(state)) return;
  const { netskraflUserId, locale, projectId, loginMethod } = state;
  auth = getAuth(app);
  if (!auth) {
    console.error("Failed to initialize Firebase Auth");
  } else {
    // Register our login function to execute once the user login is done
    onAuthStateChanged(auth,
      (user) => {
        const signedIn = (user !== null);
        if (signedIn) {
          // User is signed in
          onLoginFunc && onLoginFunc();
          // For new users, log an additional signup event
          if (state.newUser) {
            logEvent("sign_up",
              {
                locale,
                method: loginMethod,
                userid: netskraflUserId
              }
            );
          }
          // And always log a login event
          logEvent("login",
            {
              locale,
              method: loginMethod,
              userid: netskraflUserId
            }
          );
        } else {
          // No user is signed in
        }
      }
    );
  }
  // Log in to Firebase using the provided custom token
  await signInWithCustomToken(auth, firebaseToken);
  database = getDatabase(app);
  if (!database) {
    console.error("Failed to initialize Firebase Database");
  } else {
    // Database initialized successfully - flush queued callbacks
    flushDatabaseReadyQueue(state);
  }
  analytics = getAnalytics(app);
  if (!analytics) {
    console.error("Failed to initialize Firebase Analytics");
  }
  initPresence(projectId, netskraflUserId, locale);
}

function initPresence(projectId: string, userId: string, locale: string) {
  // Ensure that this user connection is recorded in Firebase
  if (!database) return;
  const connectedRef = ref(database, '.info/connected');
  // Create a unique connection entry for this user
  const connectionPath =
    projectId === "netskrafl"
    ? `connection/${userId}`
    : `connection/${locale}/${userId}`;
  const userRef = push(ref(database, connectionPath));
  onValue(connectedRef, (snapshot) => {
    if (snapshot.val()) {
      // We're connected (or reconnected)
      // When this user disconnects, remove this entry
      onDisconnect(userRef).remove();
      // Set presence
      set(userRef, true);
    }
    else
      // Unset presence
      remove(userRef);
  });
}

export function attachFirebaseListener<T>(
  path: string, func: (json: T, firstAttach: boolean) => void
) {
  // Attach a message listener to a Firebase path
  // console.log(`attachFirebaseListener(${path})`);
  if (!database) {
    // Database not ready yet - queue this attachment for later
    databaseReadyQueue.push(() => attachFirebaseListener(path, func));
    return;
  }
  let cnt = 0;
  const pathRef = ref(database, path);
  onValue(pathRef, function(snapshot) {
      // Note: we need function() here for proper closure
      // The cnt variable is used to tell the listener whether it's being
      // called upon the first attach or upon a later data change
      cnt++;
      const json: T | null = snapshot.val();
      // console.log("received on path", path, ":", json);
      if (json) {
        func(json, cnt == 1);
      }
    });
}

export function detachFirebaseListener(path: string) {
  // Detach a message listener from a Firebase path
  // console.log(`detachFirebaseListener(${path})`);
  if (!database) {
    return;
  }
  const pathRef = ref(database, path);
  off(pathRef);
}

export function logEvent(ev: string, params: any) {
  // Log a Firebase analytics event
  if (!analytics) return;
  firebaseLogEvent(analytics, ev, params);
}

export async function setFirebaseData(path: string, data: any): Promise<void> {
  // Set data at a Firebase path
  const pathRef = ref(database, path);
  await set(pathRef, data);
}

export async function pushFirebaseData(path: string, data: any): Promise<string | null> {
  // Push data to a Firebase path and return the generated key
  if (!database) return null;
  const pathRef = ref(database, path);
  const newRef = push(pathRef, data);
  return newRef.key;
}

export async function getFirebaseData<T>(path: string): Promise<T | null> {
  // Get data from a Firebase path
  if (!database) return null;
  const pathRef = ref(database, path);
  const snapshot = await get(pathRef);
  return snapshot.val();
}

export async function runFirebaseTransaction<T>(path: string, updateFunction: (currentData: T | null) => T | null): Promise<void> {
  // Run a transaction at a Firebase path
  if (!database) return;
  const pathRef = ref(database, path);
  await runTransaction(pathRef, updateFunction);
}
